import React from "react";
import { Form, Input, Row, Col, Select } from "antd";
import { useTranslation } from "react-i18next";
import { UserStatusType } from "../../../../store/auth";
import { UserStatusDisplay } from "../../../../enums/user";
import { enumToOptionsArray } from "../../../../utils";
import { Badge } from "antd";
import { getUserStatusColor } from "..";

export interface FieldTechnicianFormValues {
  firstName: string;
  lastName: string;
  email: string;
  status: UserStatusType;
  phone?: string;
  COPSMatch?: string;
  lastLoginAt?: string;
}

export interface FieldTechniciansFormProps {
  readOnlyValues?: {
    phone: string;
    COPSMatch: string;
    lastLoginAt?: string;
  };
}

export default function FieldTechniciansForm({
  readOnlyValues,
}: FieldTechniciansFormProps): React.ReactElement {
  const { t } = useTranslation();

  const renderStatusSelect = () => (
    <Select
      placeholder="Select"
      options={enumToOptionsArray(UserStatusDisplay)}
      optionRender={(option) => {
        const status = option.value as UserStatusType | undefined;
        if (!status) return option.label;
        return (
          <Badge
            color={getUserStatusColor(status)}
            text={
              <span className="capitalize-first-letter">{option.label}</span>
            }
          />
        );
      }}
      labelRender={(label) => {
        const status = label.value as UserStatusType | undefined;
        if (!status) return label.label;
        return (
          <Badge
            color={getUserStatusColor(status)}
            text={
              <span className="capitalize-first-letter">{label.label}</span>
            }
          />
        );
      }}
    />
  );

  const editableFields = [
    {
      name: "firstName",
      label: "First name",
      required: true,
      component: <Input />,
      rules: [
        { required: true },
        { min: 3, message: `Must be at least 3 characters` },
      ],
    },
    {
      name: "lastName",
      label: "Last name",
      required: true,
      component: <Input />,
      rules: [
        { required: true },
        { min: 3, message: `Must be at least 3 characters` },
      ],
    },
    {
      name: "email",
      label: "Email",
      required: true,
      component: <Input placeholder="Enter email" />,
      rules: [{ required: true }, { type: "email" }],
    },
    {
      name: "status",
      label: "Status",
      required: true,
      component: renderStatusSelect(),
      rules: [{ required: true }],
    },
  ];

  const readOnlyFields = [
    {
      name: "phone",
      label: "Phone number",
      value: readOnlyValues?.phone,
    },
    {
      name: "COPSMatch",
      label: "COPS match",
      value: readOnlyValues?.COPSMatch,
    },
    {
      name: "lastLoginAt",
      label: "Last activity",
      value: readOnlyValues?.lastLoginAt || "-",
    },
  ];

  const renderEditableFields = (fields: any[]) =>
    fields.map((f) => (
      <Col span={12} key={f.name}>
        <Form.Item name={f.name} label={f.label} rules={f.rules}>
          {f.component}
        </Form.Item>
      </Col>
    ));

  const renderReadOnlyFields = (fields: any[]) =>
    fields.map((f) => (
      <Col span={12} key={f.name}>
        <Form.Item label={f.label}>
          <Input disabled value={f.value} style={{ color: "#000000E0" }} />
        </Form.Item>
      </Col>
    ));

  return (
    <Row gutter={24}>
      {renderEditableFields(editableFields)}
      {renderReadOnlyFields(readOnlyFields)}
    </Row>
  );
}
